import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { applyMiddleware, bindActionCreators, createStore } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly'
import thunk from 'redux-thunk'
import app from './reducers'
import {historyMiddleware} from './Middlewares/History.middleware'
import {add as addToCart, reorder, toggle} from './Actions/Cart.action'
import MiniCartContainer from './Containers/MiniCart.container'
import TopLinksContainer from './Containers/TopLinks.container'
import LanguageLinksContainer from './Containers/LanguageLinks.container'
import DesktopNavigationContainer from './Containers/Navigation/Desktop.container'
import MobileNavigationContainer from './Containers/Navigation/Mobile.container'
import FacetedSearchContainer from './Containers/FacetedSearch.container'
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container'
import NotificationContainer from './Containers/Notification.container'
import ScrollHandler from './Components/ScrollHandler'
import DynamicComponent from './Components/DynamicComponent'
import GeoLocationPopup from './Components/GeoLocationPopup'
import ContactContainer from './Containers/Contact.container'
import 'glider-js/glider.min.css'
import 'glider-js/glider.min.js'
import { translate } from './Services/translation'
import './Containers/VanillaContainer';
import ImageGalleryContainer from './Containers/ImageGallery.container'

window.__litium = window.__litium || {}
const preloadState = window.__litium.preloadState || {}
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
)
// register redux's actions so we can invoke them from vanilla JS
// usage: window.__litium.reduxActions.addToCart(...);
window.__litium = {
    ...window.__litium,
    reduxActions: bindActionCreators(
        {addToCart, reorder, toggle},
        store.dispatch
    ),
    events: {
        onAddToCartButtonClick: (
            sourceDomNode,
            articleNumber,
            quantityFieldId = null
        ) => {
            const nodeIdToShowNotification = (sourceDomNode.id = Date.now())
            window.__litium.reduxActions.addToCart({
                articleNumber,
                quantity: quantityFieldId
                    ? document.getElementById(quantityFieldId).value
                    : 1,
                nodeIdToShowNotification,
                notificationMessage: translate(
                    'tooltip.cannotcombinewithpreorder'
                ),
                hash: Date.now(),
            })
        },
        onReorderClick: (orderid) => {
            if (orderid) {
                window.__litium.reduxActions.reorder({orderid})
            }
        },
    },
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents()
    },
    cache: {}, // for storing cache data for current request
}

const components = [
    {
        id: 'miniCart',
        component: MiniCartContainer,
    },
    {
        id: 'navbar',
        component: DesktopNavigationContainer,
    },
    {
        id: 'navbarMobile',
        component: MobileNavigationContainer,
    },
    {
        id: 'facetedSearch',
        component: FacetedSearchContainer,
    },
    {
        id: 'facetedSearchCompact',
        component: FacetedSearchCompactContainer,
    },
    {
        id: 'globalNotification',
        component: NotificationContainer,
    },
    {
        id: 'myPagePersons',
        import: 'PersonList.container',
    },
    {
        id: 'myPageAddresses',
        import: 'AddressList.container',
    },
    {
        id: 'checkout',
        import: 'Checkout.container',
    },
]

const renderComponents = () => {
    components.forEach((item) => {
        const element = document.getElementById(item.id)

        if (!element) {
            return
        }

        const component = item.import
            ? DynamicComponent({
                loader: () => import('./Containers/' + item.import),
            })
            : item.component

        ReactDOM.render(
            <Provider store={store}>{React.createElement(component)}</Provider>,
            element
        )
    })
}

const bootstrapComponents = () => {
    renderComponents()

    if (document.getElementById('topLinks')) {
        let links = window.__litium.topLinks
        if (links) {
            ReactDOM.render(
                <TopLinksContainer
                    values={{
                        links: links,
                        iconCssClass: 'icon-chain',
                        menuLinkTitle: 'toplinks.title',
                        showOnlyTextIfSingleLink: true,
                    }}
                />,
                document.getElementById('topLinks')
            )
        }
    }
    if (document.getElementById('languageLinks')) {
        let links = window.__litium.constants.countryLanguagePairs
        if (links) {
            ReactDOM.render(
                <LanguageLinksContainer
                    values={{
                        links: links,
                        iconCssClass: 'icon-arrow',
                        showOnlyTextIfSingleLink: true,
                        currentLanguage: window.__litium.currentLanguage,
                        currentCountryName: window.__litium.currentCountryName,
                    }}
                />,
                document.getElementById('languageLinks')
            )
        }
    }

    const geoLocationPopup = document.getElementById('GeoLocationPopup')
    if (geoLocationPopup) {
        ReactDOM.render(
            <Provider store={store}>
                <GeoLocationPopup
                    cookieName={window.__litium.geoLocationData.cookieName}
                    alertMessage={window.__litium.geoLocationData.alertMessage}
                    takeMeToGeoLocationMessage={
                        window.__litium.geoLocationData
                            .takeMeToGeoLocationMessage
                    }
                    chooseOtherCountryMessage={
                        window.__litium.geoLocationData
                            .chooseOtherCountryMessage
                    }
                    currentPageUrl={
                        window.__litium.geoLocationData.currentPageUrl
                    }
                    currentUserLocationUrl={
                        window.__litium.geoLocationData.currentUserLocationUrl
                    }
                    geoIpCountryAlpha2={
                        window.__litium.geoLocationData.geoIpCountryAlpha2
                    }
                    currentCountryAlpha2={
                        window.__litium.geoLocationData.currentCountryAlpha2
                    }
                    logo={window.__litium.geoLocationData.logo}
                />
            </Provider>,
            geoLocationPopup
        )
    }

    if (document.getElementById('scrollHandler')) {
        const onScrollDirChange = (scrollDirDown) => {
            const noScrollClass = 'header--top-row-hidden'
            const header = document.querySelector('#header')

            if (scrollDirDown) {
                header.className += ' ' + noScrollClass
            } else {
                header.className = header.className
                    .split(' ')
                    .reduce(
                        (acc, curr) =>
                            acc + (curr === noScrollClass ? '' : ' ' + curr)
                    )
            }
        }

        ReactDOM.render(
            <ScrollHandler onScrollDirChange={onScrollDirChange}/>,
            document.getElementById('scrollHandler')
        )
    }

    const lightBoxImagesElement = document.getElementById('lightBoxImages')
    if (lightBoxImagesElement) {
        const images = Array.from(
            lightBoxImagesElement.querySelectorAll('a')
        ).map((a, idx) => {
            if (a.id === "productVideo") {
                return {
                    original: a.dataset.src,
                    thumbnail: a.dataset.src,
                    type: "video",
                    index: idx,
                }
            } else {
                return {
                    original: a.dataset.src,
                    thumbnail: a.dataset.src,
                    type: "img",
                    index: idx,
                }
            }
        })

        ReactDOM.render(
            <Provider store={store}>
                <ImageGalleryContainer images={images}/>
            </Provider>,
            lightBoxImagesElement
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        })
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [
                    ...slider.querySelectorAll('.slider__block'),
                ].map((block) => {
                    return {
                        image: block.dataset.image,
                        url: block.dataset.url,
                        text: block.dataset.text,
                    }
                })
                if (values.length > 0) {
                    ReactDOM.render(<Slider values={values}/>, slider)
                }
            }
        )
    }

    if (document.querySelectorAll('.quantity-changer-placeholder').length > 0) {
        const QuantityChangerContainer = DynamicComponent({
            loader: () => import('./Containers/QuantityChanger.container'),
        })
        Array.from(
            document.querySelectorAll('.quantity-changer-placeholder')
        ).map((placeholder) => {
            const quantityFieldId = placeholder.dataset.fieldId
            if (quantityFieldId) {
                ReactDOM.render(
                    <QuantityChangerContainer
                        quantityFieldId={quantityFieldId}
                    />,
                    placeholder
                )
            }
        })
    }

    if (document.querySelectorAll('.quick-search__container').length > 0) {
        const QuickSearchContainer = DynamicComponent({
            loader: () => import('./Containers/QuickSearch.container'),
        })
        Array.from(
            document.querySelectorAll('.quick-search__container')
        ).forEach((quickSearch) => {
            ReactDOM.render(
                <Provider store={store}>
                    <QuickSearchContainer/>
                </Provider>,
                quickSearch
            )
        })
    }

    if (document.getElementById('contactform')) {
        ReactDOM.render(
            <Provider store={store}>
                <ContactContainer/>
            </Provider>,
            document.getElementById('contactform')
        )
    }

    if (document.querySelectorAll('.productblockglider').length > 0) {
        var gliderDivs = document.querySelectorAll('.productblockglider'),
            i

        for (i = 0; i < gliderDivs.length; ++i) {
            var gliderDiv = gliderDivs[i]
            var respDotsId = '#resp-dots-' + gliderDiv.dataset.systemid
            var gliderprevClass = '.gliderprev-' + gliderDiv.dataset.systemid
            var glidernextClass = '.glidernext-' + gliderDiv.dataset.systemid
            new Glider(gliderDiv, {
                // Mobile-first defaults
                slidesToShow: 2,
                slidesToScroll: 1,
                scrollLock: false,
                dots: respDotsId,
                arrows: {
                    prev: gliderprevClass,
                    next: glidernextClass,
                },
                responsive: [
                    {
                        // screens greater than >= 768px
                        breakpoint: 768,
                        settings: {
                            slidesToShow: 4,
                            slidesToScroll: 1,
                            itemWidth: 100,
                            duration: 0.25,
                        },
                    },
                    {
                        // screens greater than >= 1440px
                        breakpoint: 1440,
                        settings: {
                            slidesToShow: 4,
                            slidesToScroll: 1,
                            itemWidth: 150,
                            duration: 0.25,
                        },
                    },
                ],
            })
        }
    }

    const notifyMeButtonElems = document.getElementsByClassName("notify-me-button");
    if (notifyMeButtonElems && notifyMeButtonElems.length > 0) {
        const NotifyMeButton = DynamicComponent({
            loader: () => import('./Components/NotifyMeButton'),
        });

        for (let elem of Array.from(notifyMeButtonElems)) {
            ReactDOM.render(
                <Provider store={store}>
                    <NotifyMeButton
                        articleNumber={elem.dataset["articleNumber"]}
                        productName={elem.dataset["productName"]}
                    />
                </Provider>,
                elem.parentElement
            );
        }
    }
}

bootstrapComponents()